import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

export const readUrlFilters = (search, defaults = {}, keys) => {
  const params = new URLSearchParams(search || '');
  const managedKeys = keys || [...new Set([...Object.keys(defaults), ...params.keys()])];
  return managedKeys.reduce((result, key) => {
    const value = params.get(key);
    result[key] = value === null ? defaults[key] : value;
    return result;
  }, {});
};

export const writeUrlFilters = (search, filters, defaults = {}, keys) => {
  const params = new URLSearchParams(search || '');
  const managedKeys = keys || Object.keys(filters || {});
  managedKeys.forEach(key => {
    const value = filters?.[key];
    if (value === undefined || value === null || value === '' || value === defaults[key]) {
      params.delete(key);
    } else {
      params.set(key, String(value));
    }
  });
  const query = params.toString();
  return query ? `?${query}` : '';
};

// URL-driven filter state, matching the same back/forward-friendly rule used by tabs.
export const useUrlFilters = (defaults = {}, { keys, replace = true } = {}) => {
  const history = useHistory();
  const location = useLocation();
  const keySignature = (keys || Object.keys(defaults)).join('|');
  const defaultsSignature = JSON.stringify(defaults);
  // Signatures keep semantically identical inline options from invalidating every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filters = useMemo(() => readUrlFilters(location.search, defaults, keys), [location.search, keySignature, defaultsSignature]);

  const setFilters = useCallback(
    next => {
      const resolved = typeof next === 'function' ? next(filters) : { ...filters, ...next };
      const nextSearch = writeUrlFilters(location.search, resolved, defaults, keys);
      if (nextSearch === location.search) return;
      const navigate = replace ? history.replace : history.push;
      navigate({ ...location, search: nextSearch });
    },
    [defaults, filters, history, keys, location, replace]
  );

  return { filters, setFilters };
};

export default useUrlFilters;
